import React, { useState, useRef, useCallback } from 'react';
import './ImageUploader.css';

// Import your image analyzer directly if you have it, or create a simple mock
const analyzeImage = (img) => {
  // Make sure OpenCV is available
  if (!window.cv) {
    throw new Error('OpenCV not available');
  }
  
  try {
    console.log('[ImageUploader] Starting OpenCV image analysis');
    
    // Create a canvas to work with
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    
    // Use OpenCV directly from window.cv
    const src = window.cv.imread(img);
    const gray = new window.cv.Mat();
    window.cv.cvtColor(src, gray, window.cv.COLOR_RGBA2GRAY);
    
    // Calculate basic stats
    const mean = new window.cv.Mat();
    const stddev = new window.cv.Mat();
    window.cv.meanStdDev(gray, mean, stddev);
    const contrast = Math.round(stddev.data64F[0]);
    
    // Detect features with corner detection
    const maxCorners = 500;
    const qualityLevel = 0.01;
    const minDistance = 10;
    const blockSize = 3;
    
    // Create a matrix to store the corners
    const corners = new window.cv.Mat();
    window.cv.goodFeaturesToTrack(
      gray, corners, maxCorners, qualityLevel, minDistance, 
      new window.cv.Mat(), blockSize
    );
    
    // Store points in simple array
    const keypoints = [];
    for (let i = 0; i < corners.rows; i++) {
      const x = corners.data32F[i * 2];
      const y = corners.data32F[i * 2 + 1];
      keypoints.push({ x, y });
    }
    
    const keypointCount = keypoints.length;
    
    // Draw feature points on canvas
    window.cv.cvtColor(gray, gray, window.cv.COLOR_GRAY2RGBA);
    window.cv.imshow(canvas, gray);
    
    const ctx2 = canvas.getContext('2d');
    ctx2.fillStyle = 'rgba(0, 255, 0, 0.8)';
    ctx2.strokeStyle = 'rgba(0, 255, 0, 1)';
    
    for (const point of keypoints) {
      ctx2.beginPath();
      ctx2.arc(point.x, point.y, 5, 0, 2 * Math.PI);
      ctx2.stroke();
      
      ctx2.beginPath();
      ctx2.arc(point.x, point.y, 3, 0, 2 * Math.PI);
      ctx2.fill();
    }
    
    const featureImageUrl = canvas.toDataURL();
    
    // Calculate distribution score
    const gridSize = 5;
    const gridCells = new Array(gridSize * gridSize).fill(0);
    
    for (const point of keypoints) {
      const gridX = Math.floor(point.x / src.cols * gridSize);
      const gridY = Math.floor(point.y / src.rows * gridSize);
      
      if (gridX >= 0 && gridX < gridSize && gridY >= 0 && gridY < gridSize) {
        gridCells[gridY * gridSize + gridX]++;
      }
    }
    
    const cellsWithKeypoints = gridCells.filter(count => count > 0).length;
    const distribution = cellsWithKeypoints / (gridSize * gridSize);
    
    // Calculate scores
    let keypointScore = Math.min(100, Math.round((keypointCount / 250) * 100));
    let contrastScore = Math.min(100, Math.round((contrast / 50) * 100));
    let distributionScore = Math.round(distribution * 100);
    
    // Apply weights
    const weights = {
      keypoints: 0.3,
      contrast: 0.4,  // Reduced contrast weight slightly
      distribution: 0.3 // Increased distribution weight
    };
    
    // Apply stronger penalty for low contrast
    if (contrastScore < 30) {
      contrastScore = contrastScore * 0.3;  // More severe penalty
    } else if (contrastScore < 50) {
      contrastScore = contrastScore * 0.7;  // Moderate penalty
    }
    
    // Calculate final score
    let finalScore = Math.round(
      keypointScore * weights.keypoints +
      contrastScore * weights.contrast +
      distributionScore * weights.distribution
    );
    
    // Cap maximum score for low contrast images
    if (contrastScore < 20) {
      const maxScoreCap = 40 + (contrastScore / 20) * 15;  // Lower cap
      finalScore = Math.min(finalScore, Math.round(maxScoreCap));
    }
    
    // Apply additional penalty for poor distribution
    if (distributionScore < 60) {
      // Reduce score significantly if distribution is poor
      // Factor ranges from 0.7 (at score 0) to ~1.0 (at score 59)
      const distributionPenaltyFactor = 0.7 + (distributionScore / 60) * 0.3;
      const scoreBeforePenalty = finalScore;
      finalScore = Math.max(0, Math.round(finalScore * distributionPenaltyFactor)); // Ensure score doesn't go below 0
      console.log(`[ImageUploader] Applied distribution penalty. Score: ${scoreBeforePenalty} -> ${finalScore}, Factor: ${distributionPenaltyFactor.toFixed(2)}`);
    }
    
    // Generate recommendations based on metrics
    const recommendations = [];
    
    // Add contrast recommendations
    if (contrastScore < 20) {
      recommendations.push("⚠️ Critical contrast issue: This image has very low contrast, making it unsuitable for AR tracking. Use images with significant light/dark differences.");
    } else if (contrastScore < 50) {
      recommendations.push("Increase the contrast in your image. High contrast between light and dark areas is essential for reliable AR tracking.");
    }
    
    // Add keypoint recommendations
    if (keypointCount < 100) {
      recommendations.push("Too few feature points detected. Add more texture, detail, or complexity to your image.");
    } else if (keypointCount < 200 && contrastScore < 40) {
      recommendations.push("The combination of low contrast and limited detail will cause tracking problems. Choose an image with more distinct features.");
    }
    
    // Add distribution recommendations
    if (distributionScore < 60) {
      recommendations.push("Features are not well distributed across the image. Use images with details spread throughout the entire area.");
    }
    
    // If score is low but no specific recommendations yet
    if (finalScore < 50 && recommendations.length === 0) {
      recommendations.push("This image is not ideal for AR tracking. Consider using more complex images with distinct patterns and higher contrast.");
    }
    
    // If very high score, add positive feedback
    if (finalScore > 85 && recommendations.length === 0) {
      recommendations.push("This image has excellent tracking characteristics and should perform well in AR applications.");
    }
    
    // Clean up
    src.delete();
    gray.delete();
    mean.delete();
    stddev.delete();
    corners.delete();
    
    return {
      score: finalScore,
      keypoints: {
        count: keypointCount,
        density: keypointCount / (img.width * img.height / 1000),
        score: keypointScore
      },
      contrast: {
        value: contrast,
        score: contrastScore
      },
      distribution: {
        score: distributionScore,
        coverage: distribution
      },
      repetitiveness: {
        score: 65
      },
      starRating: (finalScore / 100) * 5,  // More accurate star rating
      recommendations: recommendations,
      featureMap: featureImageUrl
    };
  } catch (error) {
    console.error('[ImageUploader] Analysis error:', error);
    throw error;
  }
};

const ImageUploader = ({ onImageSelected, isProcessing, isReady, isFallbackMode = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  
  // Check if OpenCV is actually available
  const isOpenCVAvailable = () => {
    return window.cv && typeof window.cv === 'object' && window.cv.Mat && window.OpenCVStatus?.loaded === true;
  };
  
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);
  
  const processImage = useCallback(async (file) => {
    if (!isReady && !isFallbackMode) {
      setError('The computer vision libraries are still loading. Please wait a moment and try again.');
      return;
    }
    
    // Reset any previous errors
    setError(null);
    
    // Check if file is an image and specifically PNG or JPG/JPEG
    const acceptedTypes = ['image/png', 'image/jpeg'];
    if (!acceptedTypes.includes(file.type)) {
      setError('Please upload a PNG or JPG/JPEG image file.');
      return;
    }
    
    try {
      // Create an image element for processing
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = async () => {
        try {
          // Check if OpenCV is available
          const cvAvailable = isOpenCVAvailable();
          console.log('[ImageUploader] OpenCV available:', cvAvailable);
          
          // Only use fallback if explicitly set or OpenCV isn't available
          if (isFallbackMode || !cvAvailable) {
            console.log('[ImageUploader] Using fallback mode', 
              isFallbackMode ? '(manually selected)' : '(OpenCV not available)');
            
            onImageSelected({
              file,
              url: objectUrl,
              width: img.width,
              height: img.height,
              isFallbackMode: true
            });
            return;
          }
          
          // Process the image using OpenCV
          try {
            const results = await analyzeImage(img);
            console.log('[ImageUploader] OpenCV analysis complete with score:', results.score);
            
            // Call the parent's callback with the results
            onImageSelected({
              file,
              url: objectUrl,
              width: img.width,
              height: img.height,
              results,
              isFallbackMode: false
            });
          } catch (err) {
            console.error('[ImageUploader] Error in OpenCV analysis:', err);
            console.log('[ImageUploader] Falling back to basic mode due to OpenCV error');
            
            // Fall back to basic mode if OpenCV analysis fails
            onImageSelected({
              file,
              url: objectUrl,
              width: img.width,
              height: img.height,
              isFallbackMode: true 
            });
          }
        } catch (err) {
          console.error('[ImageUploader] Error analyzing image:', err);
          setError(`Failed to analyze the image: ${err.message}. Please try a different image or use basic mode.`);
          URL.revokeObjectURL(objectUrl); // Clean up
        }
      };
      
      img.onerror = () => {
        setError('Failed to load the image. The file might be corrupted.');
        URL.revokeObjectURL(objectUrl); // Clean up
      };
      
      // Start loading the image
      img.src = objectUrl;
    } catch (err) {
      console.error('[ImageUploader] Error processing image:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  }, [isReady, onImageSelected, isFallbackMode]);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (isProcessing) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processImage(e.dataTransfer.files[0]);
    }
  }, [isProcessing, processImage]);
  
  const handleFileChange = useCallback((e) => {
    if (isProcessing) return;
    
    if (e.target.files && e.target.files.length > 0) {
      processImage(e.target.files[0]);
    }
  }, [isProcessing, processImage]);
  
  const handleUploadClick = useCallback(() => {
    if (isProcessing) return;
    fileInputRef.current?.click();
  }, [isProcessing]);
  
  return (
    <div className="image-uploader">
      <div 
        className={`upload-area ${isDragging ? 'dragging' : ''} ${isProcessing ? 'processing' : ''} ${isFallbackMode ? 'fallback-mode' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="upload-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22V7" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M7 12L12 7L17 12" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="4" y="2" width="16" height="3" rx="1" strokeWidth="1.5"/>
          </svg>
        </div>
        
        <div className="upload-text">
          {error ? (
            <div className="upload-error">{error}</div>
          ) : isDragging ? (
            <p>Drop to analyze your image</p>
          ) : isProcessing ? (
            <p>Analyzing image...</p>
          ) : (
            <>
              <p>Drag & drop your PNG or JPG/JPEG image here</p>
              <p className="upload-subtext">
                Or select a file to analyze its AR trackability
              </p>
              {isFallbackMode && (
                <p className="upload-fallback-notice">
                  ⚠️ Running in basic mode with limited analysis
                </p>
              )}
            </>
          )}
        </div>
        
        <button className="select-file-btn" onClick={handleUploadClick}>
          Select File
        </button>
        
        <input 
          ref={fileInputRef}
          type="file" 
          className="file-input" 
          accept="image/png, image/jpeg" 
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export default ImageUploader; 